import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Navbar from "../components/profile-components/Navbar";
import Topbar from "../components/profile-components/Topbar";
import API_BASE_URL from "../config";
import useCurrentUserAccess from "../hooks/useCurrentUserAccess";
import "./SalesPages.css";

function UserProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { loading: accessLoading, platformRole, account, accountRole } = useCurrentUserAccess();

  const isPlatformAdmin = platformRole?.key_name === "platform_admin";
  const isAccountAdmin = accountRole?.key_name === "account_admin";

  const [user, setUser] = useState(null);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingAccess, setSavingAccess] = useState(false);

  const pageMode = useMemo(() => {
    if (isPlatformAdmin) return "platform";
    if (isAccountAdmin) return "subscriber";
    return "none";
  }, [isPlatformAdmin, isAccountAdmin]);

  const isPlatformUser = !!user?.platform_role_key;
  const isSubscriberUser = !!user?.account_role_key || !!user?.account_id;

  useEffect(() => {
    if (!accessLoading && pageMode !== "none") {
      loadUser();
      loadRoles();
    } else if (!accessLoading && pageMode === "none") {
      setLoading(false);
      setUser(null);
    }
  }, [id, accessLoading, pageMode]);

  const loadUser = async () => {
    try {
      setLoading(true);

      const token = localStorage.getItem("token");
      const endpoint =
        pageMode === "platform"
          ? `${API_BASE_URL}/api/users/admin/${id}`
          : `${API_BASE_URL}/api/users/account/${id}`;

      const res = await fetch(endpoint, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!res.ok) {
        throw new Error("Failed to load user");
      }

      const data = await res.json();
      setUser(data);
    } catch (err) {
      console.error("Load user error:", err.message);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const loadRoles = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE_URL}/api/roles`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!res.ok) return;

      const data = await res.json();
      const allRoles = Array.isArray(data) ? data : [];

      if (pageMode === "platform") {
        setRoles(allRoles.filter((role) => role.scope === "platform"));
      } else if (pageMode === "subscriber") {
        setRoles(allRoles.filter((role) => role.scope === "account"));
      } else {
        setRoles([]);
      }
    } catch (err) {
      console.error("Load roles error:", err.message);
    }
  };

  const handleChange = (field, value) => {
    setUser((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveAccess = async () => {
    try {
      setSavingAccess(true);

      const token = localStorage.getItem("token");

      if (pageMode === "platform") {
        if (!user?.platform_role_id) {
          alert("Platform role is required.");
          return;
        }

        const res = await fetch(`${API_BASE_URL}/api/roles/platform-role/${id}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            role_id: user.platform_role_id
          })
        });

        const data = await res.json();

        if (!res.ok) {
          throw new Error(data.error || data.message || "Failed to update platform role");
        }

        alert("Platform user updated successfully.");
        loadUser();
        return;
      }

      if (pageMode === "subscriber") {
        if (!user?.account_role_id) {
          alert("Role is required.");
          return;
        }

        const res = await fetch(`${API_BASE_URL}/api/users/account/${id}/assignment`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            role_id: user.account_role_id,
            is_active: user.is_active === 1 || user.is_active === true
          })
        });

        const data = await res.json();

        if (!res.ok) {
          throw new Error(data.error || data.message || "Failed to update user assignment");
        }

        alert("User updated successfully.");
        loadUser();
      }
    } catch (err) {
      console.error("Save access error:", err.message);
      alert(err.message);
    } finally {
      setSavingAccess(false);
    }
  };

  const getUserType = () => {
    if (isPlatformUser) return "Platform User";
    if (isSubscriberUser) return "Subscriber User";
    return "Unassigned";
  };

  const getRoleLabel = () => {
    return user?.platform_role_label || user?.account_role_label || "No Role";
  };

  const getStatusLabel = () => {
    if (isPlatformUser) return "Active";
    if (user?.is_active === 1 || user?.is_active === true) return "Active";
    if (user?.is_active === 0) return "Inactive";
    return "—";
  };

  const accountName =
    user?.account_name || account?.business_name || account?.name || "";

  if (accessLoading) {
    return (
      <>
        <Navbar />
        <Topbar />
        <div className="main-content sales-page">
          <div className="sales-section-card">Loading access...</div>
        </div>
      </>
    );
  }

  if (pageMode === "none") {
    return (
      <>
        <Navbar />
        <Topbar />
        <div className="main-content sales-page">
          <div className="sales-section-card">
            You do not have access to view this user.
          </div>
        </div>
      </>
    );
  }

  if (loading) {
    return (
      <>
        <Navbar />
        <Topbar />
        <div className="main-content sales-page">
          <div className="sales-section-card">Loading user...</div>
        </div>
      </>
    );
  }

  if (!user) {
    return (
      <>
        <Navbar />
        <Topbar />
        <div className="main-content sales-page">
          <div className="sales-section-card">User not found.</div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <Topbar />

      <div className="main-content sales-page">
        <div className="sales-hero sales-profile-hero">
          <div>
            <p className="sales-eyebrow">Agent Book CRM</p>
            <h1 className="sales-title">{user.name || "User Profile"}</h1>
            <div className="sales-hero-meta">
              <span className="sales-chip sales-chip-neutral">{getUserType()}</span>
              <span className="sales-chip sales-chip-outline">{getRoleLabel()}</span>
              {isSubscriberUser && accountName && (
                <span className="sales-chip sales-chip-neutral">{accountName}</span>
              )}
            </div>
          </div>

          <div className="sales-header-actions">
            <button className="sales-secondary-btn" onClick={() => navigate("/users")}>
              Back to Users
            </button>

            <button
              className="sales-primary-btn"
              onClick={handleSaveAccess}
              disabled={savingAccess}
            >
              {savingAccess ? "Saving..." : "Save Access"}
            </button>
          </div>
        </div>

        <div className="sales-profile-grid">
          <div className="sales-section-card sales-section-lead">
            <div className="sales-section-head">
              <h2>User Information</h2>
              <p>Basic identity and contact details.</p>
            </div>

            <div className="sales-form-fields">
              <div className="sales-field sales-field-full">
                <label>Name</label>
                <input value={user.name || ""} readOnly />
              </div>

              <div className="sales-field">
                <label>Email</label>
                <input value={user.email || ""} readOnly />
              </div>

              <div className="sales-field">
                <label>Phone</label>
                <input value={user.phone || ""} readOnly />
              </div>

              <div className="sales-field">
                <label>Title</label>
                <input value={user.title || ""} readOnly />
              </div>

              <div className="sales-field">
                <label>User Type</label>
                <input value={getUserType()} readOnly />
              </div>
            </div>
          </div>

          <div className="sales-section-card sales-section-subscription">
            <div className="sales-section-head">
              <h2>Access & Assignment</h2>
              <p>
                {pageMode === "platform"
                  ? "Manage platform role access for this internal user."
                  : "Manage role access for this user in your account."}
              </p>
            </div>

            {pageMode === "platform" ? (
              <div className="sales-form-fields">
                <div className="sales-field">
                  <label>Platform Role</label>
                  <select
                    value={user.platform_role_id || ""}
                    onChange={(e) => {
                      const selectedId = e.target.value;
                      const selectedRole = roles.find(
                        (role) => String(role.id) === String(selectedId)
                      );

                      handleChange("platform_role_id", selectedId);
                      handleChange("platform_role_label", selectedRole?.label || "");
                      handleChange("platform_role_key", selectedRole?.key_name || "");
                    }}
                  >
                    <option value="">Select Platform Role</option>
                    {roles.map((role) => (
                      <option key={role.id} value={role.id}>
                        {role.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="sales-field">
                  <label>Status</label>
                  <input value="Active" readOnly />
                </div>
              </div>
            ) : (
              <div className="sales-form-fields">
                <div className="sales-field">
                  <label>Subscriber Account</label>
                  <input value={accountName} readOnly />
                </div>

                <div className="sales-field">
                  <label>Role</label>
                  <select
                    value={user.account_role_id || ""}
                    onChange={(e) => {
                      const selectedId = e.target.value;
                      const selectedRole = roles.find(
                        (role) => String(role.id) === String(selectedId)
                      );

                      handleChange("account_role_id", selectedId);
                      handleChange("account_role_label", selectedRole?.label || "");
                      handleChange("account_role_key", selectedRole?.key_name || "");
                    }}
                  >
                    <option value="">Select Role</option>
                    {roles.map((role) => (
                      <option key={role.id} value={role.id}>
                        {role.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="sales-field">
                  <label>Status</label>
                  <select
                    value={user.is_active === 1 || user.is_active === true ? "active" : "inactive"}
                    onChange={(e) => handleChange("is_active", e.target.value === "active" ? 1 : 0)}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default UserProfile;